import { memo, useMemo } from 'react';
import {
  AllocationTab as SharedAllocationTab,
  computePlaidAllocation,
  emptyAllocation,
  isAllocationClass,
  type AllocationLoadingState,
  type AllocationTotals,
} from '@wavvest/wealth-ui';
import { useAppSelector } from '@/store/hooks';
import { selectApexAssetMap, selectApexLoading } from '@/store/features/apex/apex-selectors';
import { useApexHoldingsAccounts } from '@/hooks/useApexHoldingsAccounts';
import { useTheme } from '@/contexts/ThemeContext';
import {
  selectPlaidAccounts,
  selectPlaidHoldings,
  selectPlaidLoading,
} from '@/store/features/plaid/plaid-selectors';
import {
  generateAssetClassesView,
  getUnclassifiedApexAllocation,
  normalizeApexAssetClass,
} from '@/util/planning-util';
import { isPlaidInvestmentAccount } from '@/util/plaid-util';

// Investor portal adapter for the shared allocation tab: reads the store and
// waits for every source (holdings, asset map, linked holdings) before drawing.
const AllocationTab = () => {
  const { theme } = useTheme();
  const assetMap = useAppSelector(selectApexAssetMap);
  // Legacy per-account Apex accounts for a legacy Apex firm, or lightweight
  // {id, holdings} shims built from firm-data's blended custodian accounts
  // for a firm-data + Apex firm — see useApexHoldingsAccounts.
  const apexAccounts = useApexHoldingsAccounts();
  const plaidAccounts = useAppSelector(selectPlaidAccounts);
  const plaidHoldings = useAppSelector(selectPlaidHoldings);
  const apexLoading = useAppSelector(selectApexLoading);
  const plaidLoading = useAppSelector(selectPlaidLoading);

  const apexDataLoaded =
    apexAccounts.length === 0 ||
    (apexAccounts.every((account) => account.holdings !== undefined) && Boolean(assetMap));
  const hasPlaidInvestmentAccounts = plaidAccounts.some((account) =>
    isPlaidInvestmentAccount(account),
  );
  const plaidDataLoaded =
    !hasPlaidInvestmentAccounts || (plaidHoldings !== null && !plaidLoading.holdings);

  const managedAllocation = useMemo((): AllocationTotals | null => {
    if (apexAccounts.length === 0 || !assetMap || !apexDataLoaded) {
      return null;
    }
    const allocation = emptyAllocation();
    const treeData = generateAssetClassesView(apexAccounts, undefined, assetMap);
    treeData?.children?.forEach((child) => {
      const category = normalizeApexAssetClass(child.text);
      if (isAllocationClass(category)) {
        allocation[category] = child.valueUsd;
      } else {
        console.warn(
          `Apex asset class "${child.text}" (value: ${child.valueUsd}) does not match expected allocation categories. Adding to Other.`,
        );
        allocation.Other += child.valueUsd;
      }
    });
    // Add holdings whose symbol is not in the class_tree (buildTree drops them).
    const unclassified = getUnclassifiedApexAllocation(apexAccounts, assetMap);
    (Object.keys(allocation) as (keyof AllocationTotals)[]).forEach((key) => {
      allocation[key] += unclassified[key];
    });
    return allocation;
  }, [apexAccounts, assetMap, apexDataLoaded]);

  const linkedAllocation = useMemo(
    () => computePlaidAllocation(plaidHoldings, plaidAccounts),
    [plaidHoldings, plaidAccounts],
  );

  const loadingTip = ({ linked }: AllocationLoadingState) => {
    const linkedHoldingsLoading = linked && plaidLoading.holdings;
    if (apexLoading.holdings && linkedHoldingsLoading) {
      return 'Loading portfolio and external holdings...';
    }
    if (apexLoading.holdings) return 'Loading portfolio holdings...';
    if (linkedHoldingsLoading) return 'Loading external holdings...';
    return 'Loading allocation data...';
  };

  return (
    <SharedAllocationTab
      theme={theme}
      managedAllocation={managedAllocation}
      linkedAllocation={linkedAllocation}
      showManagedOnlyToggle={apexAccounts.length > 0}
      managedLoading={apexLoading.holdings || !apexDataLoaded}
      linkedLoading={plaidLoading.holdings || !plaidDataLoaded}
      loadingTip={loadingTip}
      centerLabel="Assets"
      centerStyle="large"
    />
  );
};

export default memo(AllocationTab);
